import React, { useEffect } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';

// Custom numbered marker for route stops - uses colorized numbers based on status
export const createNumberedIcon = (number, status, hasViolation = false, hasTimeConstraint = false) => {
  let color;
  
  if (hasViolation && status === 'pending') {
    color = '#ef4444'; // Red for time constraint violation
  } else if (status === 'successful') {
    color = '#22c55e'; // Green for served
  } else if (status === 'completed') {
    color = '#a855f7'; // Purple for completed (generic)
  } else if (status === 'unsuccessful') {
    color = '#ef4444'; // Red for failed
  } else if (status === 'skipped') {
    color = '#6b7280'; // Gray for skipped
  } else if (hasTimeConstraint && status === 'pending') {
    color = '#f59e0b'; // Orange/amber for time-constrained stops
  } else {
    color = '#ffffff'; // White for pending
  }
  
  return L.divIcon({
    className: 'custom-numbered-marker',
    html: `<div style="color: ${color}; font-weight: bold; font-size: 14px; text-shadow: -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000, 0 0 4px #000, 0 0 6px #000;">${number}</div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  });
};

// Start/End marker icons
export const createStartEndIcon = (isStart) => {
  const color = isStart ? '#4ade80' : '#f87171';
  const letter = isStart ? 'S' : 'E';
  return L.divIcon({
    className: 'custom-start-end-marker',
    html: `<div style="color: ${color}; font-weight: bold; font-size: 15px; text-shadow: -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000, 0 0 4px #000, 0 0 6px #000;">${letter}</div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  });
};

// Component to fit map bounds to markers
// Only fits bounds on initial render to avoid overriding flyTo/setView calls
export function FitBounds({ bounds }) {
  const map = useMap();
  const hasFittedRef = React.useRef(false);
  const boundsKeyRef = React.useRef('');
  
  useEffect(() => {
    if (bounds && bounds.length > 0) {
      // Create a key from the bounds to detect significant changes
      const newKey = bounds.map(b => `${b[0].toFixed(4)},${b[1].toFixed(4)}`).join('|');
      
      // Only fit if we haven't fitted yet OR if bounds significantly changed (new stops added)
      if (!hasFittedRef.current || boundsKeyRef.current !== newKey) {
        hasFittedRef.current = true;
        boundsKeyRef.current = newKey;
        map.fitBounds(bounds, { padding: [30, 30] });
      }
    }
  }, [map, bounds]);
  
  return null;
}

// Format time from 24h to 12h format
export const formatTime = (time24) => {
  if (!time24) return '';
  const [hours, minutes] = time24.split(':').map(Number);
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  return `${hours12}:${minutes.toString().padStart(2, '0')} ${ampm}`;
};

// Format duration in minutes to human readable
export const formatDuration = (minutes) => {
  if (!minutes) return '';
  const hrs = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  if (hrs === 0) return `${mins} min`;
  if (mins === 0) return `${hrs} hr`;
  return `${hrs} hr ${mins} min`;
};

// Format distance in meters to miles
export const formatDistance = (meters) => {
  if (!meters) return '';
  const miles = meters / 1609.34;
  return `${miles.toFixed(1)} mi`;
};
